<script lang="ts">
	import type { Snippet } from "svelte";
	import { Section, SectionHeader } from "#lib/web-ui/primitives/index.js";
	import Skeleton from "#lib/web-ui/ui/skeleton/skeleton.svelte";
	import CalendarIcon from "@lucide/svelte/icons/calendar";
	import UserIcon from "@lucide/svelte/icons/user";
	import ArrowRightIcon from "@lucide/svelte/icons/arrow-right";
	import type { BlogSectionProps } from "./blog.types";

	let {
		label,
		title,
		subtitle,
		posts,
		blogBaseUrl = "/blog",
		theme = "light",
		class: className,
		footer
	}: BlogSectionProps & { footer?: Snippet } = $props();

	function formatDate(dateStr: string): string {
		return new Date(dateStr).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric"
		});
	}
</script>

<Section {theme} class={className}>
	<SectionHeader {label} {title} {subtitle} />

	<div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
		{#if posts.length === 0}
			{#each Array(3) as _, i (i)}
				<div
					class="bg-card border-border flex flex-col overflow-hidden rounded border shadow-sm"
				>
					<Skeleton class="aspect-video w-full rounded-none" />
					<div class="flex flex-col gap-2 p-4">
						<div class="flex items-center gap-2">
							<Skeleton class="h-3 w-3 rounded-full" />
							<Skeleton class="h-3 w-20" />
							<Skeleton class="h-3 w-3 rounded-full" />
							<Skeleton class="h-3 w-16" />
						</div>
						<Skeleton class="h-4 w-full" />
						<Skeleton class="h-4 w-3/4" />
						<Skeleton class="mt-1 h-3 w-full" />
						<Skeleton class="h-3 w-2/3" />
					</div>
				</div>
			{/each}
		{/if}
		{#each posts as post (post.slug)}
			<a
				href="{blogBaseUrl}/{post.slug}"
				class="bg-card group border-border flex flex-col overflow-hidden rounded border shadow-sm transition-shadow hover:shadow-md"
			>
				{#if post.featureImage}
					<div class="relative aspect-video overflow-hidden">
						<img
							src={post.featureImage}
							alt={post.title}
							class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
							width="400"
							height="225"
							loading="lazy"
						/>
					</div>
				{/if}

				<div class="flex grow flex-col p-4">
					<div class="mb-2 flex items-center gap-2 text-xs">
						<UserIcon class="text-muted-foreground h-3 w-3" />
						<span class="text-muted-foreground font-medium">{post.author}</span>
						<span class="text-muted-foreground">·</span>
						<CalendarIcon class="text-muted-foreground h-3 w-3" />
						<span class="text-muted-foreground">{formatDate(post.publishedAt)}</span>
					</div>

					<h3
						class="text-card-foreground line-clamp-2 text-sm leading-snug font-semibold"
					>
						{post.title}
					</h3>

					{#if post.excerpt}
						<p class="text-muted-foreground mt-2 line-clamp-2 text-xs leading-relaxed">
							{post.excerpt}
						</p>
					{/if}
				</div>
			</a>
		{/each}
	</div>

	{#if footer}
		{@render footer()}
	{:else if blogBaseUrl}
		<div class="mt-10 text-center">
			<a
				href={blogBaseUrl}
				class="text-primary hover:text-primary/80 border-primary/30 hover:border-primary/60 inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium transition-colors"
			>
				View all posts
				<ArrowRightIcon class="h-3.5 w-3.5" />
			</a>
		</div>
	{/if}
</Section>
